import { Modal } from '../modal';
import styled from 'styled-components';
import tw from 'twin.macro';
import { IGetBookingQuery } from '../../services/bookingService/__generated__/queries.generated';
import { DateTime } from 'luxon';
import { format } from 'date-fns';

const SwapHistoryContainer = styled.div`
  ${tw`box-border p-4 sm:p-8 space-y-6 w-full sm:min-w-[600px] max-w-full overflow-x-hidden`}
`;

const Header = styled.div`
  ${tw`flex flex-col sm:flex-row justify-between items-start mb-6 gap-4`}
`;

const HeaderLeft = styled.div`
  ${tw`space-y-1 w-full sm:w-auto`}
`;

const HeaderRight = styled.div`
  ${tw`text-left sm:text-right text-sm text-text-secondary w-full sm:w-auto mt-2 sm:mt-0`}
`;

const Title = styled.h2`
  ${tw`text-base font-normal text-text-secondary`}
`;

const CarInfo = styled.div`
  ${tw`space-y-1`}
`;

const CarTitle = styled.h3`
  ${tw`text-xl font-bold break-words`}
`;

const BookingId = styled.div`
  ${tw`text-sm`}
`;

const SwapSection = styled.div`
  ${tw`mt-6 space-y-6`}
`;

const SwapItem = styled.div`
  ${tw`border-t border-border-default pt-6 first:border-t-0 first:pt-0`}
`;

const SwapHeader = styled.div`
  ${tw`flex justify-between items-start mb-4`}
`;

const SwapNumber = styled.div`
  ${tw`text-base font-medium text-text-primary`}
`;

const SwapStatus = styled.div<{ status: string }>`
  ${tw`px-3 py-1 rounded-full text-xs font-medium uppercase`}
  ${({ status }) => {
    switch (status) {
      case 'completed':
        return tw`bg-green-100 text-green-800`;
      case 'rejected':
        return tw`bg-red-100 text-red-800`;
      case 'expired':
        return tw`bg-bg-secondary text-text-primary`;
      case 'pending_guest_approval':
        return tw`bg-yellow-100 text-yellow-800`;
      default:
        return tw`bg-blue-100 text-blue-800`;
    }
  }}
`;

const CarSwapInfo = styled.div`
  ${tw`mb-4`}
`;

const CarSwapRow = styled.div`
  ${tw`flex items-center gap-2 text-sm`}
`;

const CarName = styled.span`
  ${tw`font-medium`}
`;

const Arrow = styled.span`
  ${tw`text-text-tertiary`}
`;

const SwapPeriodSection = styled.div`
  ${tw`grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 my-6`}
`;

const PeriodSection = styled.div`
  ${tw`space-y-1`}
`;

const PeriodLabel = styled.div`
  ${tw`text-sm font-medium`}
`;

const PeriodTime = styled.div`
  ${tw`text-sm break-words`}
`;

const SwapTypeBadge = styled.div<{ isMidTrip: boolean }>`
  ${tw`inline-block px-2 py-1 rounded-md text-xs font-medium mb-2`}
  ${({ isMidTrip }) =>
    isMidTrip ? tw`bg-orange-100 text-orange-800` : tw`bg-blue-100 text-blue-800`}
`;

const DetailsSection = styled.div`
  ${tw`space-y-2 mt-4`}
`;

const DetailRow = styled.div`
  ${tw`flex flex-wrap sm:flex-nowrap justify-between items-baseline py-1 text-sm`}
`;

const DetailLabel = styled.span`
  ${tw`text-text-secondary mb-1 sm:mb-0`}
`;

const DetailValue = styled.span`
  ${tw`text-text-primary font-medium text-right`}
`;

const TimelineSection = styled.div`
  ${tw`mt-4 pt-4 border-t border-border-default space-y-2`}
`;

const TimelineRow = styled.div`
  ${tw`text-sm`}
`;

const TimelineLabel = styled.span`
  ${tw`font-medium text-text-secondary`}
`;

const TimelineValue = styled.span`
  ${tw`text-text-secondary`}
`;

const NoteSection = styled.div`
  ${tw`mt-4 pt-4 border-t border-border-default`}
`;

const NoteLabel = styled.div`
  ${tw`text-sm font-medium text-text-secondary mb-1`}
`;

const NoteText = styled.div`
  ${tw`text-sm text-text-secondary italic`}
`;

const EmptyState = styled.div`
  ${tw`text-center py-8 text-text-secondary`}
`;

interface SwapHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: IGetBookingQuery['booking'];
}

export const SwapHistoryModal = ({ isOpen, onClose, booking }: SwapHistoryModalProps) => {
  const timezone = booking?.car?.location?.timezone || 'America/Los_Angeles';
  const swapHistory = ((booking as any).swapHistory || []).slice().sort((a: any, b: any) => {
    // Sort by requestedAt in ascending order (oldest first)
    const dateA = new Date(a.requestedAt).getTime();
    const dateB = new Date(b.requestedAt).getTime();
    return dateA - dateB;
  });

  const formatDateTime = (timestamp: string | number | Date | null | undefined) => {
    if (!timestamp) return 'N/A';
    try {
      const date =
        typeof timestamp === 'number'
          ? DateTime.fromMillis(timestamp)
          : DateTime.fromJSDate(new Date(timestamp));
      return date.setZone(timezone).toFormat('MMM dd, yyyy h:mm a');
    } catch {
      return 'Invalid date';
    }
  };

  const getUserDisplayName = (user: any) => {
    if (!user) return 'Unknown';
    return (
      user.displayName ||
      `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
      user.email ||
      'Unknown'
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <SwapHistoryContainer>
        <Header>
          <HeaderLeft>
            <Title>Car Swap History</Title>
            <CarInfo>
              <CarTitle>
                {booking.car?.year} {booking.car?.make} {booking.car?.model}
              </CarTitle>
            </CarInfo>
          </HeaderLeft>
          <HeaderRight>
            <BookingId>Reservation ID {booking.id}</BookingId>
            <div>
              BOOKED {format(new Date(booking.createdAt), 'dd MMM, yyyy')} AT{' '}
              {format(new Date(booking.createdAt), 'HH:mm')} UTC
            </div>
          </HeaderRight>
        </Header>

        {swapHistory.length === 0 ? (
          <EmptyState>
            <p>No swap history available for this booking.</p>
          </EmptyState>
        ) : (
          <SwapSection>
            {swapHistory.map((swap: any, index: number) => (
              <SwapItem key={swap.id}>
                <SwapHeader>
                  <SwapNumber>Swap #{index + 1}</SwapNumber>
                  <SwapStatus status={swap.status}>
                    {swap.status === 'completed'
                      ? 'COMPLETED'
                      : swap.status === 'rejected'
                        ? 'REJECTED'
                        : swap.status === 'expired'
                          ? 'EXPIRED'
                          : swap.status === 'pending_guest_approval'
                            ? 'PENDING APPROVAL'
                            : swap.status.toUpperCase()}
                  </SwapStatus>
                </SwapHeader>

                <CarSwapInfo>
                  <CarSwapRow>
                    <CarName>
                      {swap.fromCar
                        ? `${swap.fromCar.year} ${swap.fromCar.make} ${swap.fromCar.model}`
                        : swap.fromCarId || 'Unknown Car'}
                    </CarName>
                    <Arrow>→</Arrow>
                    <CarName>
                      {swap.toCar
                        ? `${swap.toCar.year} ${swap.toCar.make} ${swap.toCar.model}`
                        : swap.toCarId || 'Unknown Car'}
                    </CarName>
                  </CarSwapRow>
                </CarSwapInfo>

                {/* Swap Period Section - Similar to Trip Dates in Receipt Modal */}
                {(() => {
                  const isMidTripSwap = !!swap.swapStartTime;
                  const swapEffectiveStart = swap.swapStartTime || booking.startAt;
                  const swapEffectiveEnd = booking.endAt;

                  return (
                    <>
                      <SwapTypeBadge isMidTrip={isMidTripSwap}>
                        {isMidTripSwap ? 'MID-TRIP SWAP' : 'PRE-TRIP SWAP'}
                      </SwapTypeBadge>
                      <SwapPeriodSection>
                        <PeriodSection>
                          <PeriodLabel>SWAP EFFECTIVE FROM</PeriodLabel>
                          <PeriodTime>{formatDateTime(swapEffectiveStart)}</PeriodTime>
                          {isMidTripSwap && (
                            <div
                              style={{
                                fontSize: '11px',
                                color: 'var(--color-text-secondary)',
                                marginTop: '4px',
                              }}
                            >
                              Original trip started: {formatDateTime(booking.startAt)}
                            </div>
                          )}
                        </PeriodSection>
                        <PeriodSection>
                          <PeriodLabel>SWAP EFFECTIVE UNTIL</PeriodLabel>
                          <PeriodTime>{formatDateTime(swapEffectiveEnd)}</PeriodTime>
                        </PeriodSection>
                      </SwapPeriodSection>
                    </>
                  );
                })()}

                <TimelineSection>
                  <TimelineRow>
                    <TimelineLabel>Requested:</TimelineLabel>{' '}
                    <TimelineValue>
                      {formatDateTime(swap.requestedAt)} by{' '}
                      {getUserDisplayName(swap.requestedByUser)}
                    </TimelineValue>
                  </TimelineRow>

                  {swap.approvedAt && (
                    <TimelineRow>
                      <TimelineLabel>Approved:</TimelineLabel>{' '}
                      <TimelineValue>
                        {formatDateTime(swap.approvedAt)} by{' '}
                        {getUserDisplayName(swap.approvedByUser)}
                      </TimelineValue>
                    </TimelineRow>
                  )}

                  {swap.rejectedAt && (
                    <TimelineRow>
                      <TimelineLabel>Rejected:</TimelineLabel>{' '}
                      <TimelineValue>
                        {formatDateTime(swap.rejectedAt)} by{' '}
                        {getUserDisplayName(swap.rejectedByUser)}
                      </TimelineValue>
                    </TimelineRow>
                  )}

                  {swap.completedAt && (
                    <TimelineRow>
                      <TimelineLabel>Completed:</TimelineLabel>{' '}
                      <TimelineValue>{formatDateTime(swap.completedAt)}</TimelineValue>
                    </TimelineRow>
                  )}

                  {swap.responseDeadline && (
                    <TimelineRow>
                      <TimelineLabel>Response Deadline:</TimelineLabel>{' '}
                      <TimelineValue>{formatDateTime(swap.responseDeadline)}</TimelineValue>
                    </TimelineRow>
                  )}
                </TimelineSection>

                {swap.note && (
                  <NoteSection>
                    <NoteLabel>Note:</NoteLabel>
                    <NoteText>{swap.note}</NoteText>
                  </NoteSection>
                )}
              </SwapItem>
            ))}
          </SwapSection>
        )}
      </SwapHistoryContainer>
    </Modal>
  );
};
